import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../application/provider";
import { Button, Form, FormGroup, Input, Label, Modal, ModalBody, ModalHeader, Table } from "reactstrap";
import ProveedoresService from "../axios_services/proveedores.service";
import Loader from "../components/Loader/Loader";
import { ButtonDelete, ButtonInsert, ButtonSearch, ButtonUpdate } from "../components/Buttons/Buttons";
import ModalConfirmar from "../components/Modal/ModalConfirmar";
import "./PagoProveedores.css";

const FORM_INICIAL = {
    nombre_razon_social: "",
    tipo_documento: "RUC",
    documento: "",
    tipo_banco: "",
    numero_cuenta: "",
    cuenta_interbancaria: "",
};

const METODOS_PAGO = [
    "BCP",
    "BBVA",
    "INTERBANK",
    "SCOTIABANK",
    "BANCO DE LA NACIÓN",
    "BANBIF",
    "BANCO PICHINCHA",
    "MIBANCO",
    "CAJA HUANCAYO",
    "CAJA AREQUIPA",
    "CAJA PIURA",
    "CAJA CUSCO",
    "CAJA TRUJILLO",
    "CAJA SULLANA",
    "FINANCIERA CONFIANZA",
    "COMPARTAMOS FINANCIERA",
    "YAPE",
    "PLIN",
    "TUNKI",
    "IZIPAY YA",
    "AGORA PAY",
    "BIM",
    "EFECTIVO",
    "OTRO",
];

function GestionProveedores() {
    const [state] = useContext(AppContext);
    const [proveedores, setProveedores] = useState([]);
    const [loading, setLoading] = useState(false);
    const [buscar, setBuscar] = useState("");
    const [modalFormulario, setModalFormulario] = useState(false);
    const [proveedorSeleccionado, setProveedorSeleccionado] = useState(null);
    const [form, setForm] = useState(FORM_INICIAL);
    const [mensajeModal, setMensajeModal] = useState({ isOpen: false, titulo: "", mensaje: "", variante: "exito" });
    const [confirmacionModal, setConfirmacionModal] = useState({ isOpen: false, titulo: "", mensaje: "", textConfirmar: "Confirmar", variante: "exito", onConfirmar: null });
    const initialFetchDone = useRef(false);
    const perfilId = Number(state.user?.perfil_id || state.perfil_id);
    const puedeRegistrar = [8, 11, 12, 21, 26].includes(perfilId);

    const soloDigitos = (valor, limite) => String(valor || "").replace(/\D/g, "").slice(0, limite);
    const formatoBloques = (valor, limite = 20) => soloDigitos(valor, limite).replace(/(.{4})/g, "$1 ").trim();
    const limiteDocumento = form.tipo_documento === "RUC" ? 11 : 8;

    const mostrarMensaje = useCallback((titulo, mensaje, variante = "exito") => {
        setMensajeModal({ isOpen: true, titulo, mensaje, variante });
    }, []);

    const obtenerMensajeError = (error, fallback) => {
        const errores = error.response?.data?.errors;
        if (errores) return Object.values(errores).flat().join("\n");
        return error.response?.data?.message || fallback;
    };

    const cargarProveedores = useCallback(async () => {
        setLoading(true);
        try {
            const response = await ProveedoresService.getProveedores(buscar);
            setProveedores(response.data || []);
        } catch (error) {
            mostrarMensaje("Error", obtenerMensajeError(error, "Error al cargar proveedores"), "peligro");
            setProveedores([]);
        } finally {
            setLoading(false);
        }
    }, [buscar, mostrarMensaje]);

    useEffect(() => {
        if (!initialFetchDone.current) {
            cargarProveedores();
            initialFetchDone.current = true;
        }
    }, [cargarProveedores]);

    const abrirNuevo = () => {
        setProveedorSeleccionado(null);
        setForm(FORM_INICIAL);
        setModalFormulario(true);
    };

    const abrirEditar = (proveedor) => {
        setProveedorSeleccionado(proveedor);
        setForm({
            nombre_razon_social: proveedor.nombre_razon_social || "",
            tipo_documento: proveedor.tipo_documento || "RUC",
            documento: soloDigitos(proveedor.documento || "", proveedor.tipo_documento === "DNI" ? 8 : 11),
            tipo_banco: proveedor.tipo_banco || "",
            numero_cuenta: soloDigitos(proveedor.numero_cuenta || "", 20),
            cuenta_interbancaria: soloDigitos(proveedor.cuenta_interbancaria || "", 20),
        });
        setModalFormulario(true);
    };

    const cerrarFormulario = () => {
        setModalFormulario(false);
        setProveedorSeleccionado(null);
        setForm(FORM_INICIAL);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "tipo_documento") {
            const limite = value === "RUC" ? 11 : 8;
            setForm(prev => ({ ...prev, tipo_documento: value, documento: soloDigitos(prev.documento, limite) }));
            return;
        }

        if (name === "documento") {
            setForm(prev => ({ ...prev, documento: soloDigitos(value, prev.tipo_documento === "RUC" ? 11 : 8) }));
            return;
        }

        if (name === "numero_cuenta") {
            setForm(prev => ({ ...prev, numero_cuenta: soloDigitos(value, 20) }));
            return;
        }

        if (name === "cuenta_interbancaria") {
            setForm(prev => ({ ...prev, cuenta_interbancaria: soloDigitos(value, 20) }));
            return;
        }

        setForm(prev => ({ ...prev, [name]: value }));
    };

    const guardarProveedor = async () => {
        if (!form.nombre_razon_social || !form.documento || !form.tipo_banco) {
            mostrarMensaje("Validación", "Complete los campos obligatorios", "peligro");
            return;
        }

        if (soloDigitos(form.documento, limiteDocumento).length !== limiteDocumento) {
            mostrarMensaje("Validación", `El ${form.tipo_documento} debe tener ${limiteDocumento} dígitos`, "peligro");
            return;
        }

        setLoading(true);
        try {
            await ProveedoresService.guardarProveedor({
                ...form,
                documento: soloDigitos(form.documento, limiteDocumento),
                numero_cuenta: soloDigitos(form.numero_cuenta, 20),
                cuenta_interbancaria: soloDigitos(form.cuenta_interbancaria, 20),
            }, proveedorSeleccionado?.proveedor_id);
            cerrarFormulario();
            await cargarProveedores();
        } catch (error) {
            mostrarMensaje("Error", obtenerMensajeError(error, "Error al guardar el proveedor"), "peligro");
        } finally {
            setLoading(false);
        }
    };

    const eliminarProveedor = (proveedor) => {
        setConfirmacionModal({
            isOpen: true,
            titulo: "Eliminar proveedor",
            mensaje: `¿Está seguro de eliminar ${proveedor.nombre_razon_social}?`,
            textConfirmar: "Eliminar",
            variante: "peligro",
            onConfirmar: async () => {
                setConfirmacionModal(prev => ({ ...prev, isOpen: false, onConfirmar: null }));
                setLoading(true);
                try {
                    await ProveedoresService.eliminarProveedor(proveedor.proveedor_id);
                    await cargarProveedores();
                } catch (error) {
                    mostrarMensaje("Error", obtenerMensajeError(error, "Error al eliminar el proveedor"), "peligro");
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    return (
        <div className="caja-flujo-container pago-proveedores-scope">
            {loading && <Loader />}

            <div className="flex items-center justify-between mb-4">
                <h1>GESTIÓN DE PROVEEDORES</h1>
            </div>

            <div className="filtros">
                <div className="campo-fecha">
                    <Label>Buscar:</Label>
                    <Input value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Nombre, documento o banco" />
                </div>
            </div>

            <ButtonSearch onClick={cargarProveedores} isLoading={loading} />

            <div className="modulo-toolbar">
                {puedeRegistrar && <ButtonInsert onClick={abrirNuevo} />}
            </div>

            <div className="numero-propuestas">
                <p>N° proveedores: {proveedores.length}</p>
            </div>

            <div className="table-container">
                <Table>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>NOMBRE O RAZÓN SOCIAL</th>
                            <th>DOCUMENTO</th>
                            <th>BANCO</th>
                            <th>N° CUENTA</th>
                            <th>CCI</th>
                            <th>ACCIONES</th>
                        </tr>
                    </thead>
                    <tbody>
                        {proveedores.length === 0 ? (
                            <tr><td colSpan="7" className="sin-data">No hay proveedores registrados</td></tr>
                        ) : proveedores.map(proveedor => (
                            <tr key={proveedor.proveedor_id}>
                                <td>{proveedor.proveedor_id}</td>
                                <td>{proveedor.nombre_razon_social}</td>
                                <td>{proveedor.tipo_documento}: {proveedor.documento}</td>
                                <td>{proveedor.tipo_banco || "-"}</td>
                                <td>{proveedor.numero_cuenta || "-"}</td>
                                <td>{proveedor.cuenta_interbancaria || "-"}</td>
                                <td>
                                    <div className="acciones-container">
                                        {puedeRegistrar && <ButtonUpdate onClick={() => abrirEditar(proveedor)} />}
                                        {puedeRegistrar && <ButtonDelete onClick={() => eliminarProveedor(proveedor)} />}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            <Modal isOpen={modalFormulario} className="custom-modal" backdrop="static" size="lg" fade={false}>
                <ModalHeader className="modal-header">
                    <div className="titulo">
                        <h1>{proveedorSeleccionado ? "EDITAR PROVEEDOR" : "REGISTRAR PROVEEDOR"}</h1>
                        <Button className="close-btn" onClick={cerrarFormulario}>X</Button>
                    </div>
                </ModalHeader>
                <div className="modal-body-footer">
                    <ModalBody className="modal-body">
                        <Form className="fila" onSubmit={(e) => e.preventDefault()}>
                            <FormGroup>
                                <Label>Nombre o razón social:</Label>
                                <Input className="form-control" name="nombre_razon_social" value={form.nombre_razon_social} onChange={handleChange} />
                            </FormGroup>
                            <FormGroup>
                                <Label>Tipo documento:</Label>
                                <Input className="form-control" type="select" name="tipo_documento" value={form.tipo_documento} onChange={handleChange}>
                                    <option value="RUC">RUC</option>
                                    <option value="DNI">DNI</option>
                                </Input>
                            </FormGroup>
                            <FormGroup>
                                <Label>RUC o DNI:</Label>
                                <Input className="form-control cuenta-formateada" name="documento" value={form.documento} onChange={handleChange} inputMode="numeric" maxLength={limiteDocumento} />
                            </FormGroup>
                            <FormGroup>
                                <Label>Banco o método de pago:</Label>
                                <Input className="form-control" type="select" name="tipo_banco" value={form.tipo_banco} onChange={handleChange}>
                                    <option value="">Seleccione</option>
                                    {METODOS_PAGO.map(metodo => (
                                        <option key={metodo} value={metodo}>{metodo}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                            <FormGroup>
                                <Label>N° de cuenta o celular:</Label>
                                <Input className="form-control" name="numero_cuenta" value={form.numero_cuenta} onChange={handleChange} inputMode="numeric" maxLength={20} />
                            </FormGroup>
                            <FormGroup>
                                <Label>Cuenta interbancaria:</Label>
                                <Input className="form-control" name="cuenta_interbancaria" value={form.cuenta_interbancaria} onChange={handleChange} inputMode="numeric" maxLength={20} />
                            </FormGroup>
                            <FormGroup>
                                <div className="button-container">
                                    <Button className="button-custom" onClick={() => setConfirmacionModal({
                                        isOpen: true,
                                        titulo: "Confirmar registro",
                                        mensaje: proveedorSeleccionado ? "¿Está seguro de actualizar este proveedor?" : "¿Está seguro de guardar este proveedor?",
                                        textConfirmar: "Guardar",
                                        variante: "exito",
                                        onConfirmar: async () => {
                                            setConfirmacionModal(prev => ({ ...prev, isOpen: false, onConfirmar: null }));
                                            await guardarProveedor();
                                        },
                                    })}>
                                        GUARDAR
                                    </Button>
                                </div>
                            </FormGroup>
                        </Form>
                    </ModalBody>
                </div>
            </Modal>

            <ModalConfirmar
                isOpen={mensajeModal.isOpen}
                titulo={mensajeModal.titulo}
                mensaje={mensajeModal.mensaje}
                textConfirmar="Aceptar"
                textCancelar="Cerrar"
                variante={mensajeModal.variante}
                onConfirmar={() => setMensajeModal(prev => ({ ...prev, isOpen: false }))}
                onCancelar={() => setMensajeModal(prev => ({ ...prev, isOpen: false }))}
            />
            <ModalConfirmar
                isOpen={confirmacionModal.isOpen}
                titulo={confirmacionModal.titulo}
                mensaje={confirmacionModal.mensaje}
                textConfirmar={confirmacionModal.textConfirmar}
                textCancelar="Cancelar"
                variante={confirmacionModal.variante}
                onConfirmar={() => confirmacionModal.onConfirmar && confirmacionModal.onConfirmar()}
                onCancelar={() => setConfirmacionModal(prev => ({ ...prev, isOpen: false, onConfirmar: null }))}
            />
        </div>
    );
}

export default GestionProveedores;
